import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useOrderInfo, type OrderInfoResponse } from "@/api/orders";
import { formatMsk } from "@/lib/datetime";
import { useT } from "@/lib/i18n";
import { useToasts } from "@/store/toasts";

const STATUS_BADGE: Record<number, string> = {
  0: "bg-slate-100 text-slate-700",
  10: "bg-amber-100 text-amber-800",
  2: "bg-emerald-100 text-emerald-800",
  7: "bg-red-100 text-red-700",
  5: "bg-slate-100 text-slate-500",
};

/**
 * Extract the raw code from one entry of `order.pins`.
 *   - codes_static / codes_with_restock → list of strings
 *   - codes_combine → list of `{nominal, code}` dicts (V1 shape)
 * For an unknown shape we fall back to JSON.stringify so the user
 * can at least see/copy something rather than `[object Object]`.
 */
function pinToString(p: unknown): string {
  if (typeof p === "string") return p;
  if (p && typeof p === "object" && "code" in p) {
    const code = (p as { code: unknown }).code;
    if (typeof code === "string") return code;
  }
  try {
    return JSON.stringify(p);
  } catch {
    return String(p);
  }
}

/** Optional human label for the row (combine has nominal labels). */
function pinLabel(p: unknown): string | null {
  if (p && typeof p === "object" && "nominal" in p) {
    const n = (p as { nominal: unknown }).nominal;
    if (typeof n === "string" || typeof n === "number") return String(n);
  }
  return null;
}

async function copyToClipboard(text: string): Promise<boolean> {
  // Modern path; permission-prompted on some browsers.
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      /* fall through to legacy */
    }
  }
  // Legacy fallback - no Clipboard API or insecure context.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function downloadTextFile(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Yield a tick so Safari has time to start the download before
  // the URL is revoked.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function PinList({ pins, customId }: { pins: unknown[]; customId: string }) {
  const push = useToasts((s) => s.push);
  const t = useT();
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const codeStrings = pins.map(pinToString);
  const allText = codeStrings.join("\n");

  const onCopyOne = async (idx: number) => {
    const ok = await copyToClipboard(codeStrings[idx]);
    if (ok) {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx((cur) => (cur === idx ? null : cur)), 1500);
    } else {
      push({ kind: "error", title: t("order.copy_failed") });
    }
  };

  const onCopyAll = async () => {
    const ok = await copyToClipboard(allText);
    if (ok) {
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 1500);
    } else {
      push({ kind: "error", title: t("order.copy_all_failed") });
    }
  };

  const onDownload = () => {
    downloadTextFile(`${customId}.txt`, allText + "\n");
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-slate-500">
          {t("order.field.codes")} ({pins.length})
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onCopyAll}
            className={`btn text-xs ${
              copiedAll ? "btn-primary" : "btn-ghost"
            }`}
          >
            {copiedAll ? t("order.copied") : t("order.copy_all")}
          </button>
          <button
            type="button"
            onClick={onDownload}
            className="btn btn-ghost text-xs"
          >
            {t("order.download_txt")}
          </button>
        </div>
      </div>

      <ul className="card divide-y divide-surface-border">
        {pins.map((p, i) => {
          const code = codeStrings[i];
          const label = pinLabel(p);
          const isCopied = copiedIdx === i;
          return (
            <li
              key={i}
              className="flex items-center justify-between gap-3 px-3 py-2 sm:px-4"
            >
              <div className="min-w-0 flex-1">
                {label && (
                  <div className="text-[11px] uppercase tracking-wide text-slate-400">
                    {label}
                  </div>
                )}
                <div className="break-all font-mono text-sm text-slate-800">
                  {code}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onCopyOne(i)}
                className={`btn text-xs ${
                  isCopied ? "btn-primary" : "btn-ghost"
                } shrink-0`}
              >
                {isCopied ? t("order.copied") : t("order.copy")}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DataBlock({ data }: { data: unknown }) {
  const t = useT();
  if (data === null || data === undefined) return null;
  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wide text-slate-500">{t("order.field.payload")}</div>
      <pre className="card overflow-x-auto p-4 text-xs text-slate-800">
        {typeof data === "string" ? data : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

export function OrderDetail() {
  const { custom_id } = useParams<{ custom_id: string }>();
  const q = useOrderInfo(custom_id ?? null);
  const t = useT();

  if (q.isLoading) return <div className="text-slate-400">{t("common.loading")}</div>;
  if (q.isError || !q.data) {
    return (
      <div className="space-y-3">
        <Link to="/orders" className="text-sm text-accent">
          {t("order.back")}
        </Link>
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {t("order.not_found")}
        </div>
      </div>
    );
  }

  const o: OrderInfoResponse = q.data;
  const badge = STATUS_BADGE[o.status] ?? "bg-slate-100 text-slate-700";

  return (
    <div className="space-y-6">
      <div>
        <Link to="/orders" className="text-sm text-accent">
          {t("order.back")}
        </Link>
        <h1 className="mt-2 text-2xl font-semibold text-slate-800">
          {t("order.title")} {o.custom_id}
        </h1>
      </div>

      <div className="card grid gap-4 p-4 sm:grid-cols-2 sm:p-6">
        <Field label={t("order.field.status")}>
          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${badge}`}>
            {o.status_message}
          </span>
        </Field>
        <Field label={t("order.field.date")}>{formatMsk(o.date)}</Field>
        <Field label={t("order.field.product")}>{o.product ?? "-"}</Field>
        <Field label={t("order.field.quantity")}>{o.quantity ?? "-"}</Field>
        <Field label={t("order.field.total")}>
          {o.total_price !== null && o.total_price !== undefined
            ? `${o.total_price.toFixed(4)} USD`
            : "-"}
        </Field>
      </div>

      {o.pins && o.pins.length > 0 && (
        <PinList pins={o.pins} customId={o.custom_id} />
      )}
      {(!o.pins || o.pins.length === 0) &&
        o.data !== undefined &&
        o.data !== null && <DataBlock data={o.data} />}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-sm text-slate-800">{children}</div>
    </div>
  );
}
